import type { FounderProfileKey } from "./profiles";

export type Restedness = "almost_every_day" | "few_times_week" | "rarely" | "almost_never";

export type RecoveryPriority = "daily" | "few_times_week" | "occasionally" | "rarely";

export type Lifestyle = "athlete" | "busy_professional" | "entrepreneur" | "well_being";

export type ImproveArea = "sleep" | "stress" | "energy" | "physical_recovery" | "mental_clarity";

export type BiggestDifference =
  | "better_sleep"
  | "more_energy"
  | "less_stress"
  | "faster_recovery"
  | "greater_focus";

export interface FounderAnswers {
  restedness: Restedness;
  recoveryPriority: RecoveryPriority;
  lifestyle: Lifestyle;
  improveArea: ImproveArea;
  biggestDifference: BiggestDifference;
}

export function assignFounderProfile(a: FounderAnswers): FounderProfileKey {
  // 1. Sleep signal
  if (
    a.improveArea === "sleep" ||
    a.biggestDifference === "better_sleep" ||
    a.restedness === "almost_never"
  ) {
    return "sleep_rebuilder";
  }
  // 2. Stress signal
  if (a.improveArea === "stress" || a.biggestDifference === "less_stress") {
    return "resilience_seeker";
  }
  // 3. Energy signal
  if (a.improveArea === "energy" || a.biggestDifference === "more_energy") {
    return "energy_restorer";
  }
  // 4. Athlete + physical recovery
  if (
    a.lifestyle === "athlete" &&
    (a.improveArea === "physical_recovery" || a.biggestDifference === "faster_recovery")
  ) {
    return "performance_optimizer";
  }
  // 5. Mental-clarity / focus → performance lean
  if (a.improveArea === "mental_clarity" || a.biggestDifference === "greater_focus") {
    return "performance_optimizer";
  }
  // 6. High prioritization + strong overall
  if (
    a.recoveryPriority === "daily" &&
    (a.restedness === "almost_every_day" || a.restedness === "few_times_week")
  ) {
    return "balanced_performer";
  }
  // 7. Low prioritization + general wellness
  if (
    (a.recoveryPriority === "occasionally" || a.recoveryPriority === "rarely") &&
    a.lifestyle === "well_being"
  ) {
    return "recovery_builder";
  }
  // 8. Default
  return "recovery_builder";
}

export const RESTEDNESS_LABELS: Record<Restedness, string> = {
  almost_every_day: "Almost every day",
  few_times_week: "A few times a week",
  rarely: "Rarely",
  almost_never: "Almost never",
};

export const RECOVERY_PRIORITY_LABELS: Record<RecoveryPriority, string> = {
  daily: "Daily",
  few_times_week: "A few times a week",
  occasionally: "Occasionally",
  rarely: "Rarely",
};

export const LIFESTYLE_LABELS: Record<Lifestyle, string> = {
  athlete: "Athlete or highly active",
  busy_professional: "Busy professional",
  entrepreneur: "Entrepreneur or business owner",
  well_being: "Looking to improve overall well-being",
};

export const IMPROVE_AREA_LABELS: Record<ImproveArea, string> = {
  sleep: "Sleep",
  stress: "Stress management",
  energy: "Energy levels",
  physical_recovery: "Physical recovery",
  mental_clarity: "Mental clarity",
};

export const BIGGEST_DIFFERENCE_LABELS: Record<BiggestDifference, string> = {
  better_sleep: "Better sleep",
  more_energy: "More energy",
  less_stress: "Less stress",
  faster_recovery: "Faster recovery",
  greater_focus: "Greater focus",
};
